import { isTricareForLifeCoverage } from "@/configuration/groups/tpr/workflows/coverage-cob";
import insuranceGridRows from "@/configuration/groups/tpr/workflows/insurance-grid.json";
import type { SchedulingCallerReportedCoverageGridEntry } from "@/configuration/scheduling-insurance";
import { getInsuranceMemberIdPrefix } from "@/packages/shared/src/models/insurance-evidence";
import type {
  TprInsuranceNetworkStatus,
  TprInsuranceRequirementsOutput,
  TprInsuranceRequirementStatus,
} from "@/src/lib/workflows/steps/step-outputs";
import {
  matchesInsuranceNetworkVerificationScope,
  InsuranceRequirementStatus,
  type InsuranceNetworkVerification,
} from "@/src/models/provider-insurance-requirements";

/** One row of TPR's contracted-insurance grid (490 rows, extracted verbatim from ops' spreadsheet). */
export type TprInsuranceGridRow = {
  insuranceName: string;
  packageNumber: string;
  planNetwork: string;
  contracted: string;
  referralRequired: string;
  authorizationRequired: string;
  nonContractedExceptions: string;
  nonContractedProviders: string;
  rosterProviderException: string;
  cardiology: string;
  needsVerification: string;
  detailsNotes: string;
  sourceTab: string;
};

const INSURANCE_GRID_ROWS = insuranceGridRows as TprInsuranceGridRow[];
const MEMBER_PREFIX_VARIANT_PACKAGE_NUMBERS = new Set(["573731"]);

/** Compact, de-duplicated contracted labels for the new-patient caller-stated coverage classifier. */
export function getTprCallerReportedCoverageGrid(): SchedulingCallerReportedCoverageGridEntry[] {
  const plansByPayer = new Map<string, Set<string>>();

  for (const row of INSURANCE_GRID_ROWS) {
    const payer = row.insuranceName.trim();
    const plan = row.planNetwork.trim();

    if (!payer || row.contracted !== "Yes") continue;

    const plans = plansByPayer.get(payer) ?? new Set<string>();

    if (plan) plans.add(plan);
    plansByPayer.set(payer, plans);
  }

  return [...plansByPayer].map(([payer, plans]) => ({ payer, plans: [...plans] }));
}

export type TprInsurancePackageMatchInput = {
  payer: string | null;
  plan: string | null;
  groupNumber: string | null;
  eligibilityProductText: string | null;
};

const DUAL_COMPLETE_PATTERN = /\bdual\s+complete\b/i;
const GENERIC_PLAN_TOKENS = new Set([
  "advantage",
  "commercial",
  "exchange",
  "hmo",
  "medicaid",
  "medicare",
  "pos",
  "ppo",
  "replacement",
  "supplement",
]);
const PLAN_TOKEN_STOP_WORDS = new Set(["and", "insurance", "of", "plan", "the"]);
const PLAN_TOKEN_ALIASES: Readonly<Record<string, string>> = {
  adv: "advantage",
  diab: "diabetes",
  hrt: "heart",
  hum: "humana",
  mcr: "medicare",
  pl: "plus",
  uhc: "unitedhealthcare",
};
const AARP_SUPPLEMENTAL_PAYER_ID = "36273";
const UNITED_HEALTHCARE_PAYER_ID = "87726";
const UHC_MEDICARE_ADVANTAGE_GRID_NAME = "United Healthcare - Medicare Advantage Plans";
const GENERIC_UHC_MEDICARE_ADVANTAGE_PPO_TOKENS = new Set([
  "aarp",
  "advantage",
  "healthcare",
  "medicare",
  "ppo",
  "replacement",
  "united",
  "unitedhealthcare",
]);

function tokenizePlanLabel(value: string | null | undefined): Set<string> {
  if (!value) return new Set();

  return new Set(
    value
      .trim()
      .toLowerCase()
      .replace(/\bunited\s+health(?:care)?\b/g, "unitedhealthcare")
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length > 1 && !PLAN_TOKEN_STOP_WORDS.has(token))
      .map((token) => PLAN_TOKEN_ALIASES[token] ?? token)
  );
}

function setsEqual(left: Set<string>, right: Set<string>): boolean {
  return left.size === right.size && [...left].every((token) => right.has(token));
}

function supportsProductTokens(rowTokens: Set<string>, productTokens: Set<string>): boolean {
  return [...productTokens].every(
    (token) =>
      rowTokens.has(token) ||
      (token === "advantage" && rowTokens.has("replacement")) ||
      (token === "replacement" && rowTokens.has("advantage"))
  );
}

function uniquePackageNumber(packageNumbers: Iterable<string>): string | null {
  const unique = new Set([...packageNumbers].map((packageNumber) => packageNumber.trim()).filter(Boolean));

  return unique.size === 1 ? [...unique][0] : null;
}

function buildUniqueGroupNumberIndex(rows: readonly TprInsuranceGridRow[]): Map<string, string> {
  const tokenToPackageNumbers = new Map<string, Set<string>>();

  for (const row of rows) {
    const packageNumber = row.packageNumber.trim();

    if (!packageNumber) continue;

    for (const match of row.planNetwork.matchAll(/\bgroup\s+([A-Za-z0-9]{4,}(?:\s+and\s+[A-Za-z0-9]{4,})*)/gi)) {
      for (const token of match[1].split(/\s+and\s+/i)) {
        const key = token.toLowerCase();
        const packageNumbers = tokenToPackageNumbers.get(key) ?? new Set<string>();

        packageNumbers.add(packageNumber);
        tokenToPackageNumbers.set(key, packageNumbers);
      }
    }
  }

  const unique = new Map<string, string>();

  for (const [token, packageNumbers] of tokenToPackageNumbers) {
    const packageNumber = uniquePackageNumber(packageNumbers);

    if (packageNumber) unique.set(token, packageNumber);
  }

  return unique;
}

const UNIQUE_GROUP_NUMBER_TO_PACKAGE = buildUniqueGroupNumberIndex(INSURANCE_GRID_ROWS);

const UNIQUE_DUAL_COMPLETE_PACKAGE_NUMBER = uniquePackageNumber(
  INSURANCE_GRID_ROWS.filter((row) => DUAL_COMPLETE_PATTERN.test(row.planNetwork)).map((row) => row.packageNumber)
);
const UNIQUE_UHC_MEDICARE_ADVANTAGE_PPO_PACKAGE_NUMBER = uniquePackageNumber(
  INSURANCE_GRID_ROWS.filter(
    (row) => row.insuranceName === UHC_MEDICARE_ADVANTAGE_GRID_NAME && row.planNetwork.trim() === "PPO"
  ).map((row) => row.packageNumber)
);

function isGenericUhcMedicareAdvantagePpo(input: TprInsurancePackageMatchInput): boolean {
  const tokens = tokenizePlanLabel([input.payer, input.plan, input.eligibilityProductText].filter(Boolean).join(" "));
  const requiredTokens = ["medicare", "advantage", "ppo"];

  return (
    requiredTokens.every((token) => tokens.has(token)) &&
    (tokens.has("aarp") || tokens.has("unitedhealthcare") || (tokens.has("united") && tokens.has("healthcare"))) &&
    [...tokens].every((token) => GENERIC_UHC_MEDICARE_ADVANTAGE_PPO_TOKENS.has(token))
  );
}

function findUniqueGenericGridPackageNumber(input: TprInsurancePackageMatchInput): string | null {
  const payerTokens = tokenizePlanLabel(input.payer);
  const carrierTokens = new Set([...payerTokens].filter((token) => !GENERIC_PLAN_TOKENS.has(token)));
  const evidenceTokens = tokenizePlanLabel(
    [input.payer, input.plan, input.eligibilityProductText].filter(Boolean).join(" ")
  );
  const referralTokens = tokenizePlanLabel([input.payer, input.plan].filter(Boolean).join(" "));
  const productTokens = new Set([...evidenceTokens].filter((token) => GENERIC_PLAN_TOKENS.has(token)));
  const referralSpecificTokens = new Set(
    [...referralTokens].filter((token) => !GENERIC_PLAN_TOKENS.has(token) && !carrierTokens.has(token))
  );
  const hasLineOfBusiness = ["commercial", "exchange", "medicaid", "medicare", "supplement"].some((token) =>
    productTokens.has(token)
  );
  const hasNetwork = ["hmo", "pos", "ppo"].some((token) => productTokens.has(token));

  if (carrierTokens.size === 0 || !hasLineOfBusiness || !hasNetwork) return null;

  const compatibleRows = INSURANCE_GRID_ROWS.flatMap((row) => {
    const rowCarrierTokens = new Set(
      [...tokenizePlanLabel(row.insuranceName)].filter((token) => !GENERIC_PLAN_TOKENS.has(token))
    );

    if (!setsEqual(rowCarrierTokens, carrierTokens)) return [];

    const rowPlanTokens = tokenizePlanLabel(row.planNetwork);

    return supportsProductTokens(rowPlanTokens, productTokens) ? [{ row, rowPlanTokens }] : [];
  });
  const evidenceSpecificTokens = new Set(
    [...evidenceTokens].filter((token) => !GENERIC_PLAN_TOKENS.has(token) && !carrierTokens.has(token))
  );
  const brandedPackageNumbers = compatibleRows.flatMap(({ row, rowPlanTokens }) => {
    const rowSpecificTokens = new Set(
      [...rowPlanTokens].filter((token) => !GENERIC_PLAN_TOKENS.has(token) && !carrierTokens.has(token))
    );

    if (rowSpecificTokens.size === 0 || [...rowSpecificTokens].some((token) => !evidenceSpecificTokens.has(token))) {
      return [];
    }

    return row.packageNumber
      .split(/\s+or\s+/i)
      .map((packageNumber) => packageNumber.trim())
      .filter(Boolean);
  });

  // A branded row supported by the evidence wins over its carrier's generic row. If more than one
  // package satisfies that brand evidence, fail closed rather than falling through to a generic
  // package. Unlisted Stedi brands may still use a unique generic carrier/product row below.
  if (brandedPackageNumbers.length > 0) return uniquePackageNumber(brandedPackageNumbers);

  // Referral-specific terms are authoritative. When they do not identify a branded row, do not
  // discard them in favor of a generic row. Eligibility-only terms may be absent from TPR's grid,
  // as with Aetna Prime Extra, so they do not block the unique generic fallback.
  if (referralSpecificTokens.size > 0) return null;

  const packageNumbers = compatibleRows.flatMap(({ row, rowPlanTokens }) => {
    const rowSpecificTokens = new Set([...rowPlanTokens].filter((token) => !GENERIC_PLAN_TOKENS.has(token)));

    if (rowSpecificTokens.size > 0 || !supportsProductTokens(rowPlanTokens, productTokens)) return [];

    return row.packageNumber
      .split(/\s+or\s+/i)
      .map((packageNumber) => packageNumber.trim())
      .filter(Boolean);
  });

  return uniquePackageNumber(packageNumbers);
}

export function resolveTprEligibilityCustomerPayer(input: {
  customerPayer: string | null;
  payer: string | null;
  plan: string | null;
}): string | null {
  const productText = [input.payer, input.plan].filter(Boolean).join(" ");
  const productTokens = tokenizePlanLabel(productText);
  const isAarpMedicareAdvantage =
    productTokens.has("aarp") && productTokens.has("medicare") && productTokens.has("advantage");

  // SCAN's Arizona claims route does not support eligibility; use its eligibility route.
  if (input.customerPayer === "73172" && /\bscan[\s_-]+health[\s_-]+plan\b/i.test(productText)) {
    return "SPSCN";
  }

  // Healthcare Highways is the network on this Allied-administered policy.
  // HCHHP does not support eligibility inquiries; route them to the documented administrator.
  if (
    input.customerPayer === "HCHHP" &&
    (input.payer === "ALLIED_BENEFIT" ||
      /\ballied\s+benefit(?:s)?\s+(?:administrators?|systems?)\b/i.test(input.payer ?? "")) &&
    /\bhealthcare\s+highways\b/i.test(productText)
  ) {
    return "37308";
  }

  return input.customerPayer === AARP_SUPPLEMENTAL_PAYER_ID && isAarpMedicareAdvantage
    ? UNITED_HEALTHCARE_PAYER_ID
    : input.customerPayer;
}

export function isTprUnitedHealthcarePayer(input: {
  customerPayer: string | null;
  payer: string | null;
  plan: string | null;
}): boolean {
  if (resolveTprEligibilityCustomerPayer(input) === UNITED_HEALTHCARE_PAYER_ID) return true;

  const tokens = tokenizePlanLabel([input.payer, input.plan, input.customerPayer].filter(Boolean).join(" "));

  return tokens.has("unitedhealthcare") || (tokens.has("united") && tokens.has("healthcare"));
}

function findUniqueCarrierPlanPackageNumber(input: TprInsurancePackageMatchInput): string | null {
  const payerTokens = tokenizePlanLabel(input.payer);
  const planTokens = new Set(
    [...tokenizePlanLabel([input.plan, input.eligibilityProductText].filter(Boolean).join(" "))].filter(
      (token) => !payerTokens.has(token)
    )
  );
  const productTokens = new Set([...planTokens].filter((token) => GENERIC_PLAN_TOKENS.has(token)));
  const specificPlanTokens = new Set([...planTokens].filter((token) => !GENERIC_PLAN_TOKENS.has(token)));

  // A carrier plus a generic product type such as "Humana HMO" is not enough to safely choose a
  // package. Require at least two product-specific tokens, restrict candidates to compatible
  // product types, and return a result only when the strongest rows point to one package.
  if (payerTokens.size === 0 || specificPlanTokens.size < 2) return null;

  const scoredRows = INSURANCE_GRID_ROWS.flatMap((row) => {
    if (!setsEqual(tokenizePlanLabel(row.insuranceName), payerTokens)) return [];

    const rowPlanTokens = tokenizePlanLabel(row.planNetwork);

    if ([...productTokens].some((token) => !rowPlanTokens.has(token))) return [];

    const specificTokenOverlap = [...specificPlanTokens].filter((token) => rowPlanTokens.has(token)).length;

    return specificTokenOverlap >= 2 ? [{ row, specificTokenOverlap }] : [];
  });
  const bestScore = Math.max(0, ...scoredRows.map(({ specificTokenOverlap }) => specificTokenOverlap));
  const packageNumbers = scoredRows
    .filter(({ specificTokenOverlap }) => specificTokenOverlap === bestScore)
    .flatMap(({ row }) =>
      row.packageNumber
        .split(/\s+or\s+/i)
        .map((packageNumber) => packageNumber.trim())
        .filter(Boolean)
    );

  return uniquePackageNumber(packageNumbers);
}

export function findUniqueTprInsurancePackageNumber(input: TprInsurancePackageMatchInput): string | null {
  if (
    isTricareForLifeCoverage({
      payer: input.payer,
      plan: [input.plan, input.eligibilityProductText].filter(Boolean).join(" "),
    })
  ) {
    // For Life package mappings are maintained in the confirmed DB catalog.
    return null;
  }

  const groupNumber = input.groupNumber?.trim().toLowerCase();

  if (groupNumber) {
    const packageNumber = UNIQUE_GROUP_NUMBER_TO_PACKAGE.get(groupNumber);

    if (packageNumber) return packageNumber;
  }

  const dualCompleteHaystack = [input.payer, input.plan, input.eligibilityProductText]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(" ");

  if (DUAL_COMPLETE_PATTERN.test(dualCompleteHaystack) && UNIQUE_DUAL_COMPLETE_PACKAGE_NUMBER) {
    return UNIQUE_DUAL_COMPLETE_PACKAGE_NUMBER;
  }

  if (isGenericUhcMedicareAdvantagePpo(input) && UNIQUE_UHC_MEDICARE_ADVANTAGE_PPO_PACKAGE_NUMBER) {
    return UNIQUE_UHC_MEDICARE_ADVANTAGE_PPO_PACKAGE_NUMBER;
  }

  const genericGridPackageNumber = findUniqueGenericGridPackageNumber(input);

  if (genericGridPackageNumber) return genericGridPackageNumber;

  return findUniqueCarrierPlanPackageNumber(input);
}

export function findTprInsuranceGridRowsByPackageNumber(packageNumber: string): TprInsuranceGridRow[] {
  const needle = packageNumber.trim();

  if (!needle) return [];

  return INSURANCE_GRID_ROWS.filter((row) =>
    row.packageNumber
      .split(/\s+or\s+/i)
      .map((part) => part.trim())
      .includes(needle)
  );
}

/**
 * Some Athena packages contain mutually exclusive plan variants. Use a known member prefix only
 * when it identifies exactly one variant row; otherwise retain every package row and fail closed.
 */
function selectTprInsuranceGridRowsByMemberIdPrefix(
  packageNumber: string,
  memberIdPrefix: string | null | undefined,
  rows: TprInsuranceGridRow[]
): TprInsuranceGridRow[] {
  const prefix = memberIdPrefix?.trim().toUpperCase();

  if (!prefix || !MEMBER_PREFIX_VARIANT_PACKAGE_NUMBERS.has(packageNumber.trim())) return rows;
  const matching = findTprInsuranceGridRowsByMemberPrefix(rows, prefix);

  return matching.length === 1 ? matching : rows;
}

function findTprInsuranceGridRowsByMemberPrefix(rows: TprInsuranceGridRow[], prefix: string): TprInsuranceGridRow[] {
  return rows.filter((row) =>
    row.planNetwork
      .toUpperCase()
      .split(/[^A-Z0-9]+/)
      .includes(prefix)
  );
}

/**
 * A matched grid row is contracted for a specific receiving provider unless that provider's last
 * name appears in the row's free-text `nonContractedProviders` exception list — that field
 * overrides the row's own `contracted` column even when it says "Yes" (per-provider carve-out).
 */
export function isTprInsuranceGridRowContractedForProvider(
  row: TprInsuranceGridRow,
  receivingProviderFamilyName: string | null | undefined
): boolean {
  const familyName = receivingProviderFamilyName?.trim().toLowerCase();

  if (familyName && row.nonContractedProviders.toLowerCase().includes(familyName)) {
    return false;
  }

  return row.contracted === "Yes";
}

const NOT_REQUIRED_VALUES = new Set(["N/A", "Not Required"]);
const TPR_PCP_REFERRAL_PATTERN = /from PCP|must come from PCP|PCP Only/i;

export function tprInsuranceGridRowRequiresPcpReferral(row: TprInsuranceGridRow): boolean {
  return [row.planNetwork, row.nonContractedExceptions, row.detailsNotes].some((value) =>
    TPR_PCP_REFERRAL_PATTERN.test(value)
  );
}

export function resolveTprInsuranceRequirement(values: string[]): TprInsuranceRequirementStatus {
  const unique = new Set(values.map((value) => value.trim()).filter(Boolean));

  if (unique.size !== 1) return InsuranceRequirementStatus.ManualReview;

  const value = unique.values().next().value;

  if (value === "Required") return InsuranceRequirementStatus.Required;
  if (value && NOT_REQUIRED_VALUES.has(value)) return InsuranceRequirementStatus.NotRequired;

  return InsuranceRequirementStatus.ManualReview;
}

function resolveClinicNetwork(rows: TprInsuranceGridRow[]): TprInsuranceNetworkStatus {
  const contracted = rows.map((row) => row.contracted === "Yes");

  if (contracted.every((value) => !value)) return "out_of_network";
  if (rows.some((row) => row.nonContractedExceptions.trim())) return "manual_review";
  if (contracted.every(Boolean)) return "in_network";

  return "manual_review";
}

function resolveProviderNetwork(
  rows: TprInsuranceGridRow[],
  receivingProviderFamilyName: string | null
): TprInsuranceNetworkStatus {
  if (
    !receivingProviderFamilyName &&
    rows.some((row) => row.nonContractedProviders.trim() || row.rosterProviderException.trim())
  ) {
    return "manual_review";
  }

  const contracted = rows.map((row) => isTprInsuranceGridRowContractedForProvider(row, receivingProviderFamilyName));

  if (contracted.every(Boolean)) return "in_network";
  if (contracted.every((value) => !value)) return "out_of_network";

  return "manual_review";
}

/** Null means the package cannot be safely reused until its plan variant is identified. */
export function getTprInsuranceNetworkVariantKey(packageNumber: string, memberIdPrefix?: string | null): string | null {
  const rows = findTprInsuranceGridRowsByPackageNumber(packageNumber);

  if (MEMBER_PREFIX_VARIANT_PACKAGE_NUMBERS.has(packageNumber.trim())) {
    const prefix = memberIdPrefix?.trim().toUpperCase();

    if (!prefix) return null;
    const selected = findTprInsuranceGridRowsByMemberPrefix(rows, prefix);

    return selected.length === 1 ? prefix : null;
  }

  return new Set(rows.map((row) => row.planNetwork.trim())).size > 1 ? null : "";
}

export function resolveTprInsuranceRequirements(input: {
  packageNumber: string;
  memberIdPrefix?: string | null;
  receivingProviderFamilyName: string | null;
  selfPay: boolean;
  insuranceId?: string | null;
  providerNpi?: string | null;
  departmentId?: string | null;
  patientId?: string | null;
  networkVerifications?: readonly InsuranceNetworkVerification[];
  networkVerification?: InsuranceNetworkVerification;
}): TprInsuranceRequirementsOutput {
  if (input.selfPay) {
    return {
      packageNumber: input.packageNumber,
      planLabels: ["Self Pay"],
      networkVariantKey: null,
      clinicNetwork: "not_applicable",
      providerNetwork: "not_applicable",
      authorizationRequirement: InsuranceRequirementStatus.NotApplicable,
      referralRequirement: InsuranceRequirementStatus.NotApplicable,
      referralMustBeFromPcp: false,
      additionalSchedulingInformationRequired: false,
      reviewReasons: [],
    };
  }

  const variantKey = getTprInsuranceNetworkVariantKey(input.packageNumber, input.memberIdPrefix);
  const scope = { ...input, variantKey };
  const reusable = input.networkVerifications?.find((verification) =>
    matchesInsuranceNetworkVerificationScope(verification, scope)
  );
  const taskVerification = input.networkVerification;
  const networkVerification =
    reusable ??
    (taskVerification && matchesInsuranceNetworkVerificationScope(taskVerification, scope, true)
      ? taskVerification
      : undefined);
  const rows = selectTprInsuranceGridRowsByMemberIdPrefix(
    input.packageNumber,
    input.memberIdPrefix,
    findTprInsuranceGridRowsByPackageNumber(input.packageNumber)
  );

  if (rows.length === 0) {
    return {
      packageNumber: input.packageNumber,
      planLabels: [],
      networkVariantKey: variantKey,
      clinicNetwork: networkVerification ? "in_network" : "manual_review",
      providerNetwork: networkVerification ? "in_network" : "manual_review",
      authorizationRequirement: InsuranceRequirementStatus.ManualReview,
      referralRequirement: InsuranceRequirementStatus.ManualReview,
      referralMustBeFromPcp: false,
      additionalSchedulingInformationRequired: true,
      reviewReasons: [`Athena insurance package ${input.packageNumber} is not on TPR's contracted-plan grid.`],
      ...(networkVerification ? { networkVerification } : {}),
    };
  }

  const planLabels = [
    ...new Set(rows.map((row) => (row.planNetwork ? `${row.insuranceName} / ${row.planNetwork}` : row.insuranceName))),
  ];
  const clinicNetwork = networkVerification ? "in_network" : resolveClinicNetwork(rows);
  const providerNetwork = networkVerification
    ? "in_network"
    : clinicNetwork === "in_network"
      ? resolveProviderNetwork(rows, input.receivingProviderFamilyName)
      : "not_applicable";
  const requirementsApply = clinicNetwork === "in_network" && providerNetwork === "in_network";
  const reviewReasons: string[] = [];
  const hasNonContractedException = rows.some((row) => row.nonContractedExceptions.trim());

  if (clinicNetwork === "manual_review") {
    reviewReasons.push(
      hasNonContractedException
        ? "The matched insurance plan has a non-contracted exception that requires manual network verification."
        : "Matched insurance-grid rows disagree on clinic network status."
    );
  }

  if (providerNetwork === "manual_review") {
    reviewReasons.push(
      input.receivingProviderFamilyName
        ? "Matched insurance-grid rows disagree on receiving-provider network status."
        : "Receiving provider is missing, so provider-specific network exceptions cannot be checked."
    );
  }

  const authorizationRequirement = requirementsApply
    ? resolveTprInsuranceRequirement(rows.map((row) => row.authorizationRequired))
    : InsuranceRequirementStatus.NotApplicable;
  const referralRequirement = requirementsApply
    ? resolveTprInsuranceRequirement(rows.map((row) => row.referralRequired))
    : InsuranceRequirementStatus.NotApplicable;

  if (authorizationRequirement === InsuranceRequirementStatus.ManualReview) {
    reviewReasons.push("Authorization requirement is conditional, unspecified, or inconsistent in the grid.");
  }

  if (referralRequirement === InsuranceRequirementStatus.ManualReview) {
    reviewReasons.push("Referral requirement is conditional, unspecified, or inconsistent in the grid.");
  }

  for (const detail of new Set(
    rows
      .filter((row) => row.needsVerification === "Yes" || row.nonContractedExceptions.trim())
      .map((row) => row.detailsNotes.trim() || row.nonContractedExceptions.trim())
      .filter(Boolean)
  )) {
    reviewReasons.push(`Additional scheduling verification: ${detail}`);
  }

  return {
    packageNumber: input.packageNumber,
    planLabels,
    networkVariantKey: variantKey,
    clinicNetwork,
    providerNetwork,
    authorizationRequirement,
    referralRequirement,
    referralMustBeFromPcp: rows.some(tprInsuranceGridRowRequiresPcpReferral),
    additionalSchedulingInformationRequired:
      hasNonContractedException || rows.some((row) => row.needsVerification === "Yes"),
    reviewReasons,
    ...(networkVerification ? { networkVerification } : {}),
  };
}

/** TPR's Cigna/Oscar grid identifies package 619540 by the OSC member prefix. */
export function resolveTprMemberInsurancePackage(input: {
  subscriberId: string | null;
  payer: string | null;
  plan: string | null;
  eligibilityPayerName: string | null;
  eligibilityProductText: string | null;
}): string | null {
  if (getInsuranceMemberIdPrefix(input.subscriberId) !== "OSC") return null;

  const evidence = [input.payer, input.plan, input.eligibilityPayerName, input.eligibilityProductText]
    .filter(Boolean)
    .join(" ");

  if (!/\b(cigna|oscar)\b/i.test(evidence)) return null;
  if (/\b(hmo|epo)\b|health maintenance|exclusive provider/i.test(evidence)) return null;

  return "619540";
}

/** Banner's POS II package requires an explicit network name on this coverage. */
export function isTprAetnaPosPackageAllowed(input: {
  payer: string | null;
  plan: string | null;
  eligibilityPayerName: string | null;
  eligibilityProductText: string | null;
  insurancePackageEhrId: string;
}): boolean {
  if (!["38684", "3075504"].includes(input.insurancePackageEhrId)) return true;

  const hasBanner = [input.payer, input.plan, input.eligibilityPayerName, input.eligibilityProductText].some((value) =>
    /\bbanner\b/i.test(value ?? "")
  );

  return input.insurancePackageEhrId === "3075504" ? hasBanner : !hasBanner;
}
